import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CBadge,
  CButton,
  CDropdown,
  CDropdownMenu,
  CDropdownToggle,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilCart, cilTrash } from "@coreui/icons";
import { useCartStore } from "../../state/cartStore";
import { routePaths } from "../../routes/route-paths";
import { formatPriceUsd } from "../../lib/money";
import { useTradingStrategies } from "../../api/hooks/useTradingStrategies";
import { useMembershipTiers } from "../../api/hooks/useMembershipTiers";
import { includedByMembershipsInCart } from "../../lib/cart-overlap";
import {
  useAssetClasses,
  useReferenceValueLabel,
  useRiskProfiles,
  useTimeframes,
} from "../../api/hooks/useReference";
import { StrategyIdentityBand } from "../../pages/trading-strategies/components/StrategyIdentityBand";
import { StrategySideLabel } from "../../pages/trading-strategies/components/StrategySideLabel";

export function CartDropdown() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const strategyItems = useCartStore((s) => s.strategyItems);
  const removeStrategy = useCartStore((s) => s.removeStrategy);
  const membershipItems = useCartStore((s) => s.membershipItems);
  const removeMembership = useCartStore((s) => s.removeMembership);
  const addOnItems = useCartStore((s) => s.addOnItems);
  const removeAddOn = useCartStore((s) => s.removeAddOn);
  const upgrade = useCartStore((s) => s.upgrade);
  const removeUpgrade = useCartStore((s) => s.removeUpgrade);
  const extension = useCartStore((s) => s.extension);
  const removeExtension = useCartStore((s) => s.removeExtension);
  // The cart itself carries only name and price. Everything the row says about the strategy comes
  // from the list, which is cached for the session and already loaded by the time anything is in
  // the cart — you get there from the browse page.
  const { data: strategies } = useTradingStrategies();
  const assetClassLabel = useReferenceValueLabel(useAssetClasses().data);
  const timeframeLabel = useReferenceValueLabel(useTimeframes().data);
  const riskProfileLabel = useReferenceValueLabel(useRiskProfiles().data);
  // Frozen while a payment session is open — the store refuses edits then, so
  // the remove buttons come out rather than sitting there doing nothing.
  const checkoutLocked = useCartStore((s) => s.checkoutLocked);
  const [open, setOpen] = useState(false);

  const itemCount =
    strategyItems.length +
    membershipItems.length +
    addOnItems.length +
    (upgrade !== null ? 1 : 0) +
    (extension !== null ? 1 : 0);
  const upgradeStrategy = strategies?.find((s) => s.id === upgrade?.strategyId);
  const extensionStrategy = strategies?.find((s) => s.id === extension?.strategyId);
  const orderTotal =
    strategyItems.reduce((sum, item) => sum + item.priceUsd, 0) +
    membershipItems.reduce((sum, item) => sum + item.priceUsd, 0) +
    addOnItems.reduce((sum, item) => sum + item.priceUsd, 0);
  const { data: tiers } = useMembershipTiers();
  const includedBy = includedByMembershipsInCart(membershipItems, tiers);
  const includedLines = strategyItems.filter((item) => includedBy.has(item.strategyId));

  // Pulse the badge whenever an item is *added* (count increases). Bumping
  // pulseKey re-mounts the badge so the one-shot CSS animation runs from
  // scratch — which lets us avoid setState-in-effect (lint forbids that)
  // and removals don't celebrate. Pattern is React's "adjust state on prop
  // change" idiom, guarded by the prevCount comparison.
  const [pulseKey, setPulseKey] = useState(0);
  const [prevCount, setPrevCount] = useState(itemCount);
  if (prevCount !== itemCount) {
    if (itemCount > prevCount) {
      setPulseKey((k) => k + 1);
    }
    setPrevCount(itemCount);
  }

  const close = () => setOpen(false);

  const goTo = (path: string) => {
    close();
    navigate(path);
  };

  return (
    <CDropdown
      variant="nav-item"
      placement="bottom-end"
      visible={open}
      onShow={() => setOpen(true)}
      onHide={() => setOpen(false)}
      autoClose="outside"
      // On a phone the menu is wider than the space between the cart button and the left edge, so
      // Popper is already pushing it back on screen — that push is what put it flush against the
      // edge. Giving the push the same 0.5rem the width leaves over centres it instead. Popper
      // merges modifiers by name, so this replaces the options on its own preventOverflow.
      popperConfig={(config) => ({
        ...config,
        modifiers: [
          ...(config.modifiers ?? []),
          { name: "preventOverflow", options: { padding: 8 } },
        ],
      })}
    >
      <CDropdownToggle
        caret={false}
        title={t("common:cart.title")}
        className="position-relative"
      >
        <CIcon icon={cilCart} size="lg" />
        {itemCount > 0 && (
          <CBadge
            // The key bump on add forces the badge to re-mount so the
            // one-shot CSS pulse animation runs from scratch each time.
            key={pulseKey}
            color="danger"
            position="top-end"
            shape="rounded-pill"
            className={pulseKey > 0 ? "cart-badge-pulsing" : undefined}
            style={{ fontSize: "0.65rem" }}
          >
            {itemCount}
          </CBadge>
        )}
      </CDropdownToggle>
      {/* as="div" because the default `ul` wraps every child in its own `li`, which would put the
          list, the total and the buttons inside one flex item and defeat the layout below.
          Width lives in the stylesheet, not here, so the phone breakpoint can widen it. */}
      <CDropdownMenu as="div" className="p-0 cart-menu">
        <div className="px-3 py-2 border-bottom bg-body-secondary fw-semibold flex-shrink-0">
          {t("common:cart.title")}
        </div>

        {itemCount === 0 ? (
          <div className="p-3 text-center">
            <p className="text-body-secondary small mb-2">
              {t("common:cart.empty")}
            </p>
            <CButton
              color="primary"
              size="sm"
              onClick={() => goTo(routePaths.strategies)}
            >
              {t("common:cart.browseStrategies")}
            </CButton>
          </div>
        ) : (
          <>
            {checkoutLocked && (
              <div className="px-3 py-2 border-bottom text-body-secondary small flex-shrink-0">
                {t("common:cart.lockedDuringCheckout")}
              </div>
            )}
            <div className="cart-menu-items">
              {upgrade !== null && (
                <div className="border-bottom">
                  {upgradeStrategy ? (
                    <StrategyIdentityBand strategy={upgradeStrategy} />
                  ) : (
                    <div className="strategy-band" />
                  )}
                  <div className="d-flex align-items-start justify-content-between gap-2 px-3 py-2">
                    <div className="flex-grow-1 min-width-0">
                      <div
                        className="fw-semibold text-truncate"
                        title={upgrade.name}
                      >
                        {upgrade.name}
                      </div>
                      <StrategySideLabel direction={upgradeStrategy?.direction} iconSize={14} className="small" />
                      <div className="text-body-secondary small">
                        {t("common:cart.upgradeToYearly")} ·{" "}
                        {formatPriceUsd(upgrade.yearlyPriceUsd)}/yr
                      </div>
                    </div>
                    {!checkoutLocked && (
                      <CButton
                        color="link"
                        size="sm"
                        className="text-danger p-0 flex-shrink-0"
                        aria-label={t("common:cart.removeUpgrade", {
                          name: upgrade.name,
                        })}
                        onClick={removeUpgrade}
                      >
                        <CIcon icon={cilTrash} />
                      </CButton>
                    )}
                  </div>
                </div>
              )}
              {extension !== null && (
                <div className="border-bottom">
                  {extensionStrategy ? (
                    <StrategyIdentityBand strategy={extensionStrategy} />
                  ) : (
                    <div className="strategy-band" />
                  )}
                  <div className="d-flex align-items-start justify-content-between gap-2 px-3 py-2">
                    <div className="flex-grow-1 min-width-0">
                      <div className="fw-semibold text-truncate" title={extension.name}>
                        {extension.name}
                      </div>
                      <StrategySideLabel direction={extensionStrategy?.direction} iconSize={14} className="small" />
                      <div className="text-body-secondary small">
                        {t("common:cart.extendYear")}
                      </div>
                    </div>
                    {!checkoutLocked && (
                      <CButton
                        color="link"
                        size="sm"
                        className="text-danger p-0 flex-shrink-0"
                        aria-label={t("common:cart.removeExtension", {
                          name: extension.name,
                        })}
                        onClick={removeExtension}
                      >
                        <CIcon icon={cilTrash} />
                      </CButton>
                    )}
                  </div>
                </div>
              )}
              {membershipItems.map((item) => (
                <div key={item.tierId} className="border-bottom">
                  <div className="d-flex align-items-start justify-content-between gap-2 px-3 py-2">
                    <div className="flex-grow-1 min-width-0">
                      <div className="fw-semibold text-truncate" title={item.name}>
                        {item.name}
                      </div>
                      <div className="text-body-secondary small">{t("common:cart.membership")}</div>
                      <div className="text-body-secondary small">
                        {formatPriceUsd(item.priceUsd)}
                        {item.billingCycle === "Yearly" ? "/yr" : "/mo"}
                      </div>
                    </div>
                    {!checkoutLocked && (
                      <CButton
                        color="link"
                        size="sm"
                        className="text-danger p-0 flex-shrink-0"
                        aria-label={t("common:cart.removeItem", { name: item.name })}
                        onClick={() => removeMembership(item.tierId)}
                      >
                        <CIcon icon={cilTrash} />
                      </CButton>
                    )}
                  </div>
                </div>
              ))}
              {addOnItems.map((item) => (
                <div key={item.addOnId} className="border-bottom">
                  <div className="d-flex align-items-start justify-content-between gap-2 px-3 py-2">
                    <div className="flex-grow-1 min-width-0">
                      <div className="fw-semibold text-truncate" title={item.name}>
                        {item.name}
                      </div>
                      <div className="text-body-secondary small">{t("common:cart.addOn")}</div>
                      <div className="text-body-secondary small">
                        {formatPriceUsd(item.priceUsd)}
                        {item.billingCycle === "Yearly" ? "/yr" : "/mo"}
                      </div>
                    </div>
                    {!checkoutLocked && (
                      <CButton
                        color="link"
                        size="sm"
                        className="text-danger p-0 flex-shrink-0"
                        aria-label={t("common:cart.removeItem", { name: item.name })}
                        onClick={() => removeAddOn(item.addOnId)}
                      >
                        <CIcon icon={cilTrash} />
                      </CButton>
                    )}
                  </div>
                </div>
              ))}
              {strategyItems.map((item) => {
                const strategy = strategies?.find(
                  (s) => s.id === item.strategyId
                );
                return (
                  <div key={item.strategyId} className="border-bottom">
                    {/* Same band as the cart page and the browse card. Drawn empty until the
                        strategy arrives so the menu does not resize under the pointer. */}
                    {strategy ? (
                      <StrategyIdentityBand strategy={strategy} />
                    ) : (
                      <div className="strategy-band" />
                    )}
                    <div className="d-flex align-items-start justify-content-between gap-2 px-3 py-2">
                      <div className="flex-grow-1 min-width-0">
                        <div
                          className="fw-semibold text-truncate"
                          title={item.name}
                        >
                          {item.name}
                        </div>
                        <StrategySideLabel direction={strategy?.direction} iconSize={14} className="small" />
                        {/* Words, not the pills the cart page uses: at this width three badges wrap
                            onto two lines and the menu grows taller than the page behind it. */}
                        {strategy && (
                          <div className="text-body-secondary small text-truncate">
                            {assetClassLabel(strategy.assetClass)} ·{" "}
                            {timeframeLabel(strategy.timeframe)} ·{" "}
                            {riskProfileLabel(strategy.riskProfile)}
                          </div>
                        )}
                        <div className="text-body-secondary small">
                          {formatPriceUsd(item.priceUsd)}
                          {item.billingCycle === "Yearly" ? "/yr" : "/mo"}
                        </div>
                        {includedBy.has(item.strategyId) && (
                          <div className="text-warning-emphasis small">
                            {t("common:cart.includedInCartMembership", { name: includedBy.get(item.strategyId) })}
                          </div>
                        )}
                      </div>
                      {!checkoutLocked && (
                        <CButton
                          color="link"
                          size="sm"
                          className="text-danger p-0 flex-shrink-0"
                          aria-label={t("common:cart.removeItem", {
                            name: item.name,
                          })}
                          onClick={() => removeStrategy(item.strategyId)}
                        >
                          <CIcon icon={cilTrash} />
                        </CButton>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* An upgrade or an extension has its own figures, which the cart page gives. */}
            {upgrade === null && extension === null && (
              <div className="d-flex justify-content-between px-3 py-2 border-bottom flex-shrink-0">
                <strong>{t("common:cart.orderTotal")}</strong>
                <strong>{formatPriceUsd(orderTotal)}</strong>
              </div>
            )}
            {includedLines.length > 0 && (
              <div className="px-3 pt-2 small text-warning-emphasis flex-shrink-0">
                {t("common:cart.removeToCheckOut", { names: includedLines.map((item) => item.name).join(", ") })}
              </div>
            )}
            <div className="d-flex gap-2 p-3 flex-shrink-0">
              <CButton
                color="secondary"
                variant="outline"
                className="flex-grow-1"
                onClick={() => goTo(routePaths.cart)}
              >
                {t("common:cart.viewCart")}
              </CButton>
              <CButton
                color="primary"
                className="flex-grow-1"
                disabled={includedLines.length > 0}
                onClick={() => goTo(routePaths.checkout)}
              >
                {t("common:cart.checkout")}
              </CButton>
            </div>
          </>
        )}
      </CDropdownMenu>
    </CDropdown>
  );
}
